import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { KSK2070_R01Dto, ItemSubtotalRow, DateSubtotalRow, GridRowData, SubtotalData, DateSubtotalData } from './Dto_KSK2070_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface KSK2070_R01Filter {
    compCd: string;
    productDateRange: DateRange | undefined;
    orderType: string;
    workplaceCd: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
}

// PRD3040_R01Dto에 isSubtotal 속성 추가 (기본값: false)
export interface ExtendedKSK2070_R01Dto extends KSK2070_R01Dto {
    isSubtotal: false;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_KSK2070_R01 {
    KSK2070_R01Filter: KSK2070_R01Filter;
    KSK2070_R01PastFilter: KSK2070_R01Filter;
    setKSK2070_R01Filter: (filter: Partial<KSK2070_R01Filter>) => void;
    setKSK2070_R01PastFilter: (filter: Partial<KSK2070_R01Filter>) => void;

    // === 리스트 관련 ===
    KSK2070_R01List: ExtendedKSK2070_R01Dto[];
    setKSK2070_R01List: (KSK2070_R01List: ExtendedKSK2070_R01Dto[]) => void;
    insertKSK2070_R01List: (data: ExtendedKSK2070_R01Dto[], index?: number) => void;
    removeKSK2070_R01List: (index: number) => void;
    updateKSK2070_R01List: (data: ExtendedKSK2070_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedKSK2070_R01List: string[];
    setSelectedKSK2070_R01List: (idList: string[]) => void;
    addSelectedKSK2070_R01List: (idList: string[]) => void;
    removeSelectedKSK2070_R01List: (idList?: string[]) => void;
    initSelectedKSK2070_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

    // === 통합 소계 관련 ===
    showSubtotals: boolean;              // 통합 소계 표시 여부 (품목별 + 생산일자별)
    setShowSubtotals: (show: boolean) => void;
    subtotalData: SubtotalData[];        // 품목별 소계 데이터
    setSubtotalData: (data: SubtotalData[]) => void;
    dateSubtotalData: DateSubtotalData[]; // 생산일자별 소계 데이터
    setDateSubtotalData: (data: DateSubtotalData[]) => void;
    gridRowData: GridRowData[];
    setGridRowData: (data: GridRowData[]) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultKSK2070_R01Filter: KSK2070_R01Filter = {
    compCd: '',
    productDateRange: defaultDateRange,
    orderType: '',
    workplaceCd: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
};

/**
 * Zustand 스토어 정의
 */
export const useKSK2070_R01Store = create<Store_KSK2070_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_KSK2070_R01, 'KSK2070_R01Filter' | 'KSK2070_R01PastFilter'>) => 
        (filter: Partial<KSK2070_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        KSK2070_R01Filter: defaultKSK2070_R01Filter,
        KSK2070_R01PastFilter: defaultKSK2070_R01Filter,
        setKSK2070_R01Filter: createFilterSetter('KSK2070_R01Filter'),
        setKSK2070_R01PastFilter: createFilterSetter('KSK2070_R01PastFilter'),

        // === 리스트 관련 ===
        KSK2070_R01List: [],
        setKSK2070_R01List: (KSK2070_R01List: ExtendedKSK2070_R01Dto[]) => set({ KSK2070_R01List }),
        insertKSK2070_R01List: (data: ExtendedKSK2070_R01Dto[], index?: number) => set((state) => {
            const _KSK2070_R01List = [...state.KSK2070_R01List];
            
            if (index !== undefined) {
                _KSK2070_R01List.splice(index, 0, ...data);
            } else {
                _KSK2070_R01List.push(...data);
            }
            return { ...state, KSK2070_R01List: _KSK2070_R01List };
        }),
        removeKSK2070_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            KSK2070_R01List: state.KSK2070_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateKSK2070_R01List: (data: ExtendedKSK2070_R01Dto, index: number) => set((state) => {
            const _KSK2070_R01List = [...state.KSK2070_R01List];
            _KSK2070_R01List[index] = data;
            return { ...state, KSK2070_R01List: _KSK2070_R01List };
        }),

        // === 선택 관련 ===
        selectedKSK2070_R01List: [],
        setSelectedKSK2070_R01List: (idList: string[]) => set({ selectedKSK2070_R01List: idList }),
        addSelectedKSK2070_R01List: (idList: string[]) => set((state) => {
            const _selectedKSK2070_R01List = [...state.selectedKSK2070_R01List];
            _selectedKSK2070_R01List.push(...idList.filter(value => !_selectedKSK2070_R01List.includes(value)));
            return { ...state, selectedKSK2070_R01List: _selectedKSK2070_R01List };
        }),
        removeSelectedKSK2070_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedKSK2070_R01List: [] };
            }
            
            return {
                ...state,
                selectedKSK2070_R01List: removeItemsFromArray(state.selectedKSK2070_R01List, idList),
            };
        }),
        initSelectedKSK2070_R01List: () => set({ selectedKSK2070_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
        setAllCount: (allCount: number) => set({ allCount }),

        // === 통합 소계 관련 ===
        showSubtotals: false,
        setShowSubtotals: (show: boolean) => set({ showSubtotals: show }),
        subtotalData: [],
        setSubtotalData: (data: SubtotalData[]) => set({ subtotalData: data }),
        dateSubtotalData: [],
        setDateSubtotalData: (data: DateSubtotalData[]) => set({ dateSubtotalData: data }),
        gridRowData: [],
        setGridRowData: (data: GridRowData[]) => set({ gridRowData: data }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            KSK2070_R01Filter: defaultKSK2070_R01Filter,
            KSK2070_R01PastFilter: defaultKSK2070_R01Filter,
            KSK2070_R01List: [],
            selectedKSK2070_R01List: [],
            columnState: null,
            allCount: 0,
		
            showSubtotals: false,
            subtotalData: [],
            dateSubtotalData: [],
            gridRowData: []
        })),
    };
});
